import React, { useState, useEffect } from 'react';
import type { EmployeeDto, EmployeeLeaveCreditDto } from '../../../services/api';
import { hrEmployeesApi } from '../../../services/api';

export type EmployeeFormData = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  street_address: string;
  city: string;
  state_province: string;
  postal_code: string;
  department: string;
  job_title: string;
  employment_type: string;
  employee_type: string;
  hire_date: string;
  status: string;
  salary_type: string;
  pay_rate: string;
  bank_name: string;
  account_number: string;
  routing_number: string;
  emergency_contact_name: string;
  emergency_contact_relationship: string;
  emergency_contact_phone: string;
  sss_number: string;
  pagibig_number: string;
  philhealth_number: string;
  tin_number: string;
  gsis_number: string;
  benefits_notes: string;
  create_account: boolean;
  password: string;
};

const initialForm: EmployeeFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  street_address: '',
  city: '',
  state_province: '',
  postal_code: '',
  department: '',
  job_title: '',
  employment_type: 'Full Time',
  employee_type: 'Regular',
  hire_date: '',
  status: 'Active',
  salary_type: 'Salary',
  pay_rate: '0',
  bank_name: '',
  account_number: '',
  routing_number: '',
  emergency_contact_name: '',
  emergency_contact_relationship: '',
  emergency_contact_phone: '',
  sss_number: '',
  pagibig_number: '',
  philhealth_number: '',
  tin_number: '',
  gsis_number: '',
  benefits_notes: '',
  create_account: false,
  password: '',
};

type TabId = 'personal' | 'employment' | 'payment' | 'emergency' | 'benefits' | 'account';

interface AddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EmployeeFormData) => Promise<void>;
  editEmployee?: EmployeeDto | null;
  viewOnly?: boolean;
}

const employmentTypes = ['Full Time', 'Part Time', 'Contract', 'Intern', 'Temporary'];
const employeeTypes = ['Trainee', 'Regular', 'Supervisor', 'Manager'];
const statusOptions = ['Active', 'On Leave', 'Terminated', 'Suspended'];
const salaryTypes = ['Salary', 'Hourly', 'Monthly'];

export default function AddEmployeeModal({
  open,
  onClose,
  onSubmit,
  editEmployee,
  viewOnly = false,
}: AddEmployeeModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('personal');
  const [form, setForm] = useState<EmployeeFormData>(() =>
    editEmployee
      ? {
          first_name: editEmployee.first_name,
          last_name: editEmployee.last_name,
          email: editEmployee.email,
          phone: editEmployee.phone || '',
          date_of_birth: editEmployee.date_of_birth || '',
          street_address: editEmployee.street_address || '',
          city: editEmployee.city || '',
          state_province: editEmployee.state_province || '',
          postal_code: editEmployee.postal_code || '',
          department: editEmployee.department || '',
          job_title: editEmployee.job_title || '',
          employment_type: editEmployee.employment_type || 'Full Time',
          employee_type: editEmployee.employee_type || 'Regular',
          hire_date: editEmployee.hire_date || '',
          status: editEmployee.status || 'Active',
          salary_type: editEmployee.salary_type || 'Salary',
          pay_rate: String(editEmployee.pay_rate ?? 0),
          bank_name: editEmployee.bank_name || '',
          account_number: editEmployee.account_number || '',
          routing_number: editEmployee.routing_number || '',
          emergency_contact_name: editEmployee.emergency_contact_name || '',
          emergency_contact_relationship: editEmployee.emergency_contact_relationship || '',
          emergency_contact_phone: editEmployee.emergency_contact_phone || '',
          sss_number: editEmployee.sss_number || '',
          pagibig_number: editEmployee.pagibig_number || '',
          philhealth_number: editEmployee.philhealth_number || '',
          tin_number: editEmployee.tin_number || '',
          gsis_number: editEmployee.gsis_number || '',
          benefits_notes: editEmployee.benefits_notes || '',
          create_account: false,
          password: '',
        }
      : initialForm
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resetPasswordNew, setResetPasswordNew] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [leaveCredits, setLeaveCredits] = useState<EmployeeLeaveCreditDto[] | null>(null);
  const [loadingLeaveCredits, setLoadingLeaveCredits] = useState(false);

  useEffect(() => {
    if (open) {
      setResetPasswordSuccess(false);
      setResetPasswordError('');
      setResetPasswordNew('');
      setResetPasswordConfirm('');
      if (editEmployee) {
        setForm({
          first_name: editEmployee.first_name,
          last_name: editEmployee.last_name,
          email: editEmployee.email,
          phone: editEmployee.phone || '',
          date_of_birth: editEmployee.date_of_birth || '',
          street_address: editEmployee.street_address || '',
          city: editEmployee.city || '',
          state_province: editEmployee.state_province || '',
          postal_code: editEmployee.postal_code || '',
          department: editEmployee.department || '',
          job_title: editEmployee.job_title || '',
          employment_type: editEmployee.employment_type || 'Full Time',
          employee_type: editEmployee.employee_type || 'Regular',
          hire_date: editEmployee.hire_date || '',
          status: editEmployee.status || 'Active',
          salary_type: editEmployee.salary_type || 'Salary',
          pay_rate: String(editEmployee.pay_rate ?? 0),
          bank_name: editEmployee.bank_name || '',
          account_number: editEmployee.account_number || '',
          routing_number: editEmployee.routing_number || '',
          emergency_contact_name: editEmployee.emergency_contact_name || '',
          emergency_contact_relationship: editEmployee.emergency_contact_relationship || '',
          emergency_contact_phone: editEmployee.emergency_contact_phone || '',
          sss_number: editEmployee.sss_number || '',
          pagibig_number: editEmployee.pagibig_number || '',
          philhealth_number: editEmployee.philhealth_number || '',
          tin_number: editEmployee.tin_number || '',
          gsis_number: editEmployee.gsis_number || '',
          benefits_notes: editEmployee.benefits_notes || '',
          create_account: false,
          password: '',
        });
        // Load leave credits for existing employee
        if (editEmployee.id) {
          setLoadingLeaveCredits(true);
          hrEmployeesApi
            .getLeaveCredits(editEmployee.id)
            .then((res) => {
              setLeaveCredits(res.data ?? []);
            })
            .catch(() => {
              setLeaveCredits([]);
            })
            .finally(() => {
              setLoadingLeaveCredits(false);
            });
        }
      } else {
        setForm(initialForm);
        setLeaveCredits(null);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- sync form when modal opens; editEmployee read when open is true

  const isEdit = !!editEmployee;
  const isView = viewOnly && isEdit;
  const title = isView ? 'View Employee' : isEdit ? 'Edit Employee' : 'Add New Employee';
  const submitLabel = isEdit ? 'Save Employee' : 'Add Employee';
  const readOnly = isView;

  const update = (field: keyof EmployeeFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field as string]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    setErrors({});
    if (!form.first_name.trim()) {
      setErrors({ first_name: 'First name is required.' });
      setActiveTab('personal');
      return;
    }
    if (!form.last_name.trim()) {
      setErrors({ last_name: 'Last name is required.' });
      setActiveTab('personal');
      return;
    }
    if (!form.email.trim()) {
      setErrors({ email: 'Email is required.' });
      setActiveTab('account');
      return;
    }
    if (!isEdit && form.create_account && form.password.length < 8) {
      setErrors({ password: 'Password must be at least 8 characters.' });
      setActiveTab('account');
      return;
    }
    setSaving(true);
    try {
      await onSubmit(form);
      setForm(initialForm);
      setActiveTab('personal');
      onClose();
    } catch (err: unknown) {
      const res = err as { errors?: Record<string, string[]> };
      if (res?.errors) {
        const flat: Record<string, string> = {};
        Object.entries(res.errors).forEach(([k, v]) => {
          if (Array.isArray(v) && v[0]) flat[k] = v[0];
        });
        setErrors(flat);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetPasswordError('');
    setResetPasswordSuccess(false);
    if (resetPasswordNew.length < 8) {
      setResetPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (resetPasswordNew !== resetPasswordConfirm) {
      setResetPasswordError('Passwords do not match.');
      return;
    }
    if (!editEmployee?.id) return;
    setResettingPassword(true);
    try {
      const res = await hrEmployeesApi.resetPassword(editEmployee.id, {
        password: resetPasswordNew,
        password_confirmation: resetPasswordConfirm,
      });
      const body = res as { message?: string; errors?: Record<string, string[]> };
      if (body.message === 'Password reset successfully.') {
        setResetPasswordSuccess(true);
        setResetPasswordNew('');
        setResetPasswordConfirm('');
      } else {
        const errMsg = body.errors?.password?.[0] ?? body.message ?? 'Failed to reset password.';
        setResetPasswordError(errMsg);
      }
    } catch {
      setResetPasswordError('Network error. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'personal', label: 'Personal' },
    { id: 'employment', label: 'Employment' },
    { id: 'payment', label: 'Payment' },
    { id: 'emergency', label: 'Emergency' },
    { id: 'benefits', label: 'Benefits' },
    { id: 'account', label: 'Account' },
  ];

  if (!open) return null;

  const inputClass = (field: string) =>
    `w-full px-3 py-2 rounded-lg border text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${
      readOnly ? 'bg-gray-50 cursor-default' : ''
    } ${errors[field] ? 'border-red-300' : 'border-gray-200'}`;
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col bg-white rounded-2xl shadow-xl border border-gray-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h2 id="modal-title" className="text-xl font-bold text-gray-900">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex border-b border-gray-100 shrink-0 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-gray-900 bg-white border-b-2 border-brand-500 text-brand-700 -mb-px'
                  : 'text-gray-500 hover:text-gray-700 bg-gray-50/80'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Personal */}
            {activeTab === 'personal' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.first_name}
                      onChange={(e) => update('first_name', e.target.value)}
                      placeholder="John"
                      className={inputClass('first_name')}
                      readOnly={readOnly}
                    />
                    {errors.first_name && <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.last_name}
                      onChange={(e) => update('last_name', e.target.value)}
                      placeholder="Doe"
                      className={inputClass('last_name')}
                      readOnly={readOnly}
                    />
                    {errors.last_name && <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Date of Birth</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={form.date_of_birth}
                      onChange={(e) => update('date_of_birth', e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Street address</label>
                  <input
                    type="text"
                    value={form.street_address}
                    onChange={(e) => update('street_address', e.target.value)}
                    placeholder="Street address"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>City</label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => update('city', e.target.value)}
                      placeholder="City"
                      className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                      readOnly={readOnly}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>State</label>
                    <input
                      type="text"
                      value={form.state_province}
                      onChange={(e) => update('state_province', e.target.value)}
                      placeholder="State"
                      className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                      readOnly={readOnly}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Postal code</label>
                    <input
                      type="text"
                      value={form.postal_code}
                      onChange={(e) => update('postal_code', e.target.value)}
                      placeholder="Postal code"
                      className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Employment */}
            {activeTab === 'employment' && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Department</label>
                  <input
                    type="text"
                    value={form.department}
                    onChange={(e) => update('department', e.target.value)}
                    placeholder="e.g., Engineering"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Job Title</label>
                  <input
                    type="text"
                    value={form.job_title}
                    onChange={(e) => update('job_title', e.target.value)}
                    placeholder="e.g., Software Engineer"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Employment Type</label>
                  <select
                    value={form.employment_type}
                    onChange={(e) => update('employment_type', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    disabled={readOnly}
                  >
                    {employmentTypes.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Type</label>
                  <select
                    value={form.employee_type}
                    onChange={(e) => update('employee_type', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    disabled={readOnly}
                  >
                    {employeeTypes.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Hire Date</label>
                  <input
                    type="date"
                    value={form.hire_date}
                    onChange={(e) => update('hire_date', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={form.status}
                    onChange={(e) => update('status', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    disabled={readOnly}
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Payment */}
            {activeTab === 'payment' && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Salary Type</label>
                  <select
                    value={form.salary_type}
                    onChange={(e) => update('salary_type', e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    disabled={readOnly}
                  >
                    {salaryTypes.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Pay Rate</label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.pay_rate}
                    onChange={(e) => update('pay_rate', e.target.value)}
                    placeholder="0"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Bank Name</label>
                  <input
                    type="text"
                    value={form.bank_name}
                    onChange={(e) => update('bank_name', e.target.value)}
                    placeholder="Bank name"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Account Number</label>
                  <input
                    type="text"
                    value={form.account_number}
                    onChange={(e) => update('account_number', e.target.value)}
                    placeholder="Account number"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Routing Number</label>
                  <input
                    type="text"
                    value={form.routing_number}
                    onChange={(e) => update('routing_number', e.target.value)}
                    placeholder="Routing #"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
              </div>
            )}

            {/* Emergency */}
            {activeTab === 'emergency' && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Contact Name</label>
                  <input
                    type="text"
                    value={form.emergency_contact_name}
                    onChange={(e) => update('emergency_contact_name', e.target.value)}
                    placeholder="Emergency contact name"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Relationship</label>
                  <input
                    type="text"
                    value={form.emergency_contact_relationship}
                    onChange={(e) => update('emergency_contact_relationship', e.target.value)}
                    placeholder="e.g., Spouse"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Phone</label>
                  <input
                    type="text"
                    value={form.emergency_contact_phone}
                    onChange={(e) => update('emergency_contact_phone', e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
              </div>
            )}

            {/* Benefits (SSS, Pag-IBIG, PhilHealth, etc.) */}
            {activeTab === 'benefits' && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>SSS Number</label>
                  <input
                    type="text"
                    value={form.sss_number}
                    onChange={(e) => update('sss_number', e.target.value)}
                    placeholder="SSS number"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Pag-IBIG Number</label>
                  <input
                    type="text"
                    value={form.pagibig_number}
                    onChange={(e) => update('pagibig_number', e.target.value)}
                    placeholder="Pag-IBIG number"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>PhilHealth Number</label>
                  <input
                    type="text"
                    value={form.philhealth_number}
                    onChange={(e) => update('philhealth_number', e.target.value)}
                    placeholder="PhilHealth number"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>TIN (Tax Identification Number)</label>
                  <input
                    type="text"
                    value={form.tin_number}
                    onChange={(e) => update('tin_number', e.target.value)}
                    placeholder="TIN"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>GSIS Number</label>
                  <input
                    type="text"
                    value={form.gsis_number}
                    onChange={(e) => update('gsis_number', e.target.value)}
                    placeholder="GSIS number (if applicable)"
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                <div>
                  <label className={labelClass}>Benefits Notes</label>
                  <textarea
                    value={form.benefits_notes}
                    onChange={(e) => update('benefits_notes', e.target.value)}
                    placeholder="Additional benefits or notes"
                    rows={3}
                    className={`w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none ${readOnly ? 'bg-gray-50 cursor-default' : ''}`}
                    readOnly={readOnly}
                  />
                </div>
                {isEdit && (
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-800">Leave Credits</h3>
                      <span className="text-xs text-gray-500">
                        {editEmployee?.employee_type
                          ? `Based on employee type: ${editEmployee.employee_type}`
                          : 'Based on employee type'}
                      </span>
                    </div>
                    {loadingLeaveCredits ? (
                      <p className="text-sm text-gray-500">Loading leave credits...</p>
                    ) : !leaveCredits || leaveCredits.length === 0 ? (
                      <p className="text-sm text-gray-500">
                        No leave credits assigned for this employee&apos;s type yet.
                      </p>
                    ) : (
                      <div className="overflow-hidden rounded-lg border border-gray-100">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Credit
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Type
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Days
                              </th>
                              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Remaining
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {leaveCredits.map((c) => (
                              <tr key={c.id}>
                                <td className="px-3 py-2 text-gray-800">{c.credit_name}</td>
                                <td className="px-3 py-2 text-gray-600 capitalize">{c.type}</td>
                                <td className="px-3 py-2 text-right text-gray-600">{c.days}</td>
                                <td className="px-3 py-2 text-right text-gray-900 font-medium">
                                  {c.remaining_days}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Configure default leave credits per employee type in HR & Payroll → Settings → Leave Credits.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Account (email & password for employee login) */}
            {activeTab === 'account' && (
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="john@company.com"
                    className={inputClass('email')}
                    readOnly={readOnly || !!isEdit}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                  <p className="mt-1 text-xs text-gray-500">Used for employee portal login.</p>
                </div>

                {readOnly ? null : !isEdit ? (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="create_account"
                        checked={form.create_account}
                        onChange={(e) => update('create_account', e.target.checked)}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <label htmlFor="create_account" className="text-sm font-medium text-gray-700">
                        Create login account for employee portal (time in / time out)
                      </label>
                    </div>
                    {form.create_account && (
                      <div>
                        <label className={labelClass}>Password (min 8 characters)</label>
                        <input
                          type="password"
                          value={form.password}
                          onChange={(e) => update('password', e.target.value)}
                          placeholder="••••••••"
                          className={inputClass('password')}
                        />
                        {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                      </div>
                    )}
                  </>
                ) : readOnly ? null : editEmployee?.has_login_account ? (
                  <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-800">Reset password</h3>
                    <p className="text-xs text-gray-600">
                      Set a new password for this employee&apos;s login account. They will use it to sign in to the employee portal.
                    </p>
                    {resetPasswordSuccess && (
                      <p className="text-sm text-emerald-600 font-medium">Password reset successfully.</p>
                    )}
                    {resetPasswordError && (
                      <p className="text-sm text-red-600">{resetPasswordError}</p>
                    )}
                    <form onSubmit={handleResetPassword} className="space-y-3">
                      <div>
                        <label className={labelClass}>New password</label>
                        <input
                          type="password"
                          value={resetPasswordNew}
                          onChange={(e) => { setResetPasswordNew(e.target.value); setResetPasswordError(''); }}
                          placeholder="Min 8 characters"
                          minLength={8}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Confirm new password</label>
                        <input
                          type="password"
                          value={resetPasswordConfirm}
                          onChange={(e) => { setResetPasswordConfirm(e.target.value); setResetPasswordError(''); }}
                          placeholder="Confirm password"
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={resettingPassword}
                        className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-60 transition-colors"
                      >
                        {resettingPassword ? 'Resetting...' : 'Reset password'}
                      </button>
                    </form>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">This employee does not have a login account. Account creation is only available when adding a new employee.</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 shrink-0">
            {readOnly ? (
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2.5 text-sm font-semibold text-white bg-brand-600 rounded-lg hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-60 transition-colors"
                >
                  {saving ? 'Saving...' : submitLabel}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
